if (!window.CTCartData) {
  window.CTCartData = {
    cart: null,
    freshUntil: 0,
    pending: null,

    invalidate() {
      this.cart = null;
      this.freshUntil = 0;
    },

    fetch() {
      if (this.cart && Date.now() < this.freshUntil) return Promise.resolve(this.cart);
      if (this.pending) return this.pending;

      const pendingRequest = fetch(`${routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
          return response.json();
        })
        .then((cart) => {
          this.cart = cart;
          this.freshUntil = Date.now() + 1500;
          return cart;
        })
        .finally(() => {
          if (this.pending === pendingRequest) this.pending = null;
        });

      this.pending = pendingRequest;
      return pendingRequest;
    },
  };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const addonVariantId = this.selectedGiftAddonVariantId;
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const giftBundleId = addonVariantId ? this.createGiftBundleId() : null;
        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
        const sectionsUrl = window.location.pathname;

        this.clearGiftAddonError();

        /*
         * Give the main item and its Gift Box the same private bundle ID.
         * A new ID is created for every Add to cart action, so Shopify keeps
         * identical products/add-ons as separate, correctly paired lines.
         */
        if (giftBundleId) {
          formData.append('properties[_ct_bundle_id]', giftBundleId);
          formData.append('properties[_ct_has_gift_box]', 'true');
        }

        if (this.cart) {
          formData.append('sections', sections);
          formData.append('sections_url', sectionsUrl);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        window.CTCartData?.invalidate();

        const hasUploadedFile = Array.from(
          this.form.querySelectorAll('input[type="file"]')
        ).some((input) => input.files?.length);

        const canBatchGiftAddon = Boolean(addonVariantId && !hasUploadedFile);
        const canUseJsonAdd = !hasUploadedFile;

        const addRequest = canUseJsonAdd
          ? this.addProductAndGiftAddon({
              formData,
              variantId,
              quantity,
              addonVariantId,
              giftBundleId,
              sections,
              sectionsUrl,
            })
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

        addRequest
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            let finalResponse = response;

            if (addonVariantId && !canBatchGiftAddon) {
              try {
                const addonResponse = await this.addGiftAddon(
                  addonVariantId,
                  quantity,
                  giftBundleId,
                  variantId,
                  sections,
                  sectionsUrl
                );
                if (addonResponse) {
                  finalResponse = this.mergeCartDisplayResponse(response, addonResponse);
                }
              } catch (addonError) {
                console.error(addonError);
                this.showGiftAddonError(
                  addonError.message || 'The gift box could not be added. Please try again.'
                );
              }
            }

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const runPostAddTasks = () => {
              this.resolveCartLinesUpdate(linesUpdateDeferred);

              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: finalResponse,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            };
            const renderCartFeedback = () => {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(finalResponse);
              });

              // Let the browser paint the notification before starting
              // analytics and quantity-rule refresh requests.
              requestAnimationFrame(() => setTimeout(runPostAddTasks));
            };
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(renderCartFeedback);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderCartFeedback();
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      get giftAddon() {
        return this.closest('product-info')?.querySelector('[data-ct-gift-addon]') || null;
      }

      get selectedGiftAddonVariantId() {
        const addon = this.giftAddon;
        const selectedChoice = addon?.querySelector('[data-ct-gift-addon-choice]:checked');

        if (!addon || selectedChoice?.value !== 'yes') return null;
        return addon.dataset.variantId || null;
      }

      clearGiftAddonError() {
        const errorElement = this.giftAddon?.querySelector('[data-ct-gift-addon-error]');
        if (!errorElement) return;

        errorElement.textContent = '';
        errorElement.hidden = true;
      }

      showGiftAddonError(message) {
        const errorElement = this.giftAddon?.querySelector('[data-ct-gift-addon-error]');
        if (!errorElement) return;

        errorElement.textContent = message;
        errorElement.hidden = false;
      }

      createGiftBundleId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();

        return `ct-gift-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      async addProductAndGiftAddon({
        formData,
        variantId,
        quantity,
        addonVariantId,
        giftBundleId,
        sections,
        sectionsUrl,
      }) {
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);

          if (!match || value === '' || value instanceof File) continue;
          properties[match[1]] = String(value);
        }

        const mainItem = {
          id: Number(variantId),
          quantity,
          properties,
        };
        const sellingPlan = formData.get('selling_plan');

        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [
              mainItem,
              ...(addonVariantId ? [{
                id: Number(addonVariantId),
                quantity,
                properties: {
                  _ct_bundle_id: giftBundleId,
                  _ct_addon_type: 'gift_box',
                  _ct_parent_variant_id: String(variantId),
                },
              }] : []),
            ],
            sections,
            sections_url: sectionsUrl,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'The product could not be added.');
        }

        const addedMainItem = data.items?.find(
          (item) => String(item.variant_id ?? item.id) === String(variantId)
        );

        if (!addedMainItem) throw new Error('The main product was not returned.');

        return {
          ...data,
          ...addedMainItem,
          key: addedMainItem.key,
          sections: data.sections,
        };
      }

      async addGiftAddon(variantId, quantity, bundleId, parentVariantId, sections, sectionsUrl) {
        const body = {
          id: Number(variantId),
          quantity,
          properties: {
            _ct_bundle_id: bundleId,
            _ct_addon_type: 'gift_box',
            _ct_parent_variant_id: String(parentVariantId),
          },
        };

        if (sections.length > 0) {
          body.sections = sections;
          body.sections_url = sectionsUrl;
        }

        const addonResponse = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const addonData = await addonResponse.json();

        if (!addonResponse.ok || addonData.status) {
          throw new Error(
            addonData.description || addonData.message || 'The gift box could not be added.'
          );
        }

        return addonData;
      }

      mergeCartDisplayResponse(mainProductResponse, addonResponse) {
        const mainSections = mainProductResponse.sections || {};
        const addonSections = addonResponse.sections || {};
        const mergedSections = {
          ...mainSections,
          ...addonSections,
        };

        /*
         * The second request contains the newest cart totals, but a cart
         * notification should still present the main product the customer
         * intentionally added instead of showing Gift Box as the popup item.
         */
        if (this.cart?.matches('cart-notification')) {
          Object.entries(mainSections).forEach(([sectionId, sectionHtml]) => {
            if (sectionId.includes('cart-notification-product')) {
              mergedSections[sectionId] = sectionHtml;
            }
          });
        }

        return {
          ...mainProductResponse,
          sections: mergedSections,
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = window.CTCartData
          ? window.CTCartData.fetch()
          : typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
